import Vue from 'vue'

/*
 * En GET, les paramètres de requête passent par `data`, pas par `params` : auth.fetch recompose
 * sa configuration axios à la main et ne recopie pas `params` (voir ExportRepository).
 */

/**
 * La politique de commande d'une organisation : ce qu'on peut y commander, qui valide, les
 * licences, les acomptes, l'usage des données et les badges. Réglages versionnés.
 */
class CommissioningPolicyRepository {
  constructor() {
    /** Les lectures en cours, par politique : une seconde demande identique attend la première au lieu de repartir. */
    this.reading = new Map()
  }

  // GET /api/commissioning/policy-catalogue
  catalogue() {
    return this.fetch({ url: '/api/commissioning/policy-catalogue', method: 'get' })
  }

  // GET /api/commissioning/policies/{organizationId}[?version=] — null tant que rien n'est publié
  // Deux écrans qui demandent la même politique au même moment (la page et sa mise en route) partagent la même lecture,
  // gardée quelques secondes ; une publication l'oublie aussitôt.
  get(organizationId, version = null, policyId = null) {
    const params = { ...(version !== null ? { version } : {}), ...(policyId ? { policyId } : {}) }
    const key = `${organizationId}|${version ?? ''}|${policyId ?? ''}`
    if (!this.reading.has(key)) {
      const request = this.fetch({ url: `/api/commissioning/policies/${organizationId}`, method: 'get', data: params })
        .catch(error => {
          this.reading.delete(key)
          throw error
        })
      request.then(() => setTimeout(() => this.reading.get(key) === request && this.reading.delete(key), 5000), () => {})
      this.reading.set(key, request)
    }
    // Chacun sa copie : un écran qui modifie ce qu'il a lu ne change pas ce que lit l'autre.
    return this.reading.get(key).then(data => (data == null ? data : JSON.parse(JSON.stringify(data))))
  }

  // POST /api/commissioning/policies/{organizationId}
  // basedOnVersion : la version chargée par l'écran — un collègue qui a publié entre-temps fait refuser l'envoi.
  publish(organizationId, settings, basedOnVersion, policyId = null) {
    this.reading.clear()
    return this.fetch({
      url: `/api/commissioning/policies/${organizationId}` + (policyId ? `?policyId=${policyId}` : ''),
      method: 'post',
      data: { settings, basedOnVersion },
    })
  }

  // GET /api/commissioning/policies/{organizationId}/licence-subscriptions — de quoi licencier un équipement
  licenceSubscriptions(organizationId) {
    return this.fetch({ url: `/api/commissioning/policies/${organizationId}/licence-subscriptions`, method: 'get' })
  }

  // GET /api/commissioning/policies/{organizationId}/vehicle-formulas — les formules véhicule connectables
  // [{ kind, mode, subscriptionId, subscriptionName, formulaId, formulaName, type }]
  vehicleFormulas(organizationId) {
    return this.fetch({ url: `/api/commissioning/policies/${organizationId}/vehicle-formulas`, method: 'get' })
  }

  // GET /api/commissioning/sites/{organizationId} : les sites de l'organisation (jamais un domicile), pour l'emplacement imposé
  // [{ id, name, address }]
  sites(organizationId) {
    return this.fetch({ url: `/api/commissioning/sites/${organizationId}`, method: 'get' })
  }

  fetch(options) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch(options)
        .then(response => resolve(response.data))
        .catch(err => reject(err))
    })
  }
}

export default new CommissioningPolicyRepository()
